"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, MapPin, Sparkles, PhoneCall } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "item-1",
  question: "Why is local SEO critical for Cheltenham and Gloucestershire businesses?",
  answer: "Cheltenham is a vibrant commercial hub with high local competition across professional services, hospitality, healthcare, and retail. When local customers search for services 'near me' or in Cheltenham, Google prioritizes the Local 3-Pack and localized organic results. Without dedicated local SEO, your business misses out on high-intent local buyers actively seeking immediate solutions in your immediate area."
}, {
  id: "item-2",
  question: "How does local SEO differ from national or international SEO strategies?",
  answer: "National SEO focuses on broad keyword rankings across entire countries, whereas local SEO optimizes your digital footprint for hyper-targeted geo-location signals. This includes Google Business Profile management, local citation consistency across UK directories, location-specific schema markups, and earning local Gloucestershire backlinks that prove your physical relevance to Google."
}, {
  id: "item-3",
  question: "How quickly can my Cheltenham business expect to see local SEO results?",
  answer: "Google Business Profile optimizations and technical fixes often produce initial visibility improvements within 30 to 60 days. However, sustainable top rankings in competitive Cheltenham categories typically mature over 3 to 6 months of targeted local link acquisition, citation building, and content refinement. I provide clear, transparent monthly progress tracking so you see real-world growth."
}, {
  id: "item-4",
  question: "Why work directly with me as a consultant instead of hiring a traditional SEO agency?",
  answer: "When you work with me, you get direct senior-level expertise on every audit, strategy, and execution phase. Agencies frequently delegate accounts to junior account managers or use automated cookie-cutter templates. As an independent consultant, I limit my client count to deliver tailored, high-touch local strategy and direct line access whenever you need advice."
}, {
  id: "item-5",
  question: "Can local SEO help my business rank in nearby areas like Gloucester, Stroud, and Tewkesbury?",
  answer: "Yes. I design strategic multi-location frameworks and tailored area landing pages that establish organic authority across surrounding Gloucestershire towns. By building geo-targeted content and signals, we capture customers searching in neighbouring districts without resorting to spammy doorway pages."
}, {
  id: "item-6",
  question: "What is included in your local Google Business Profile optimization?",
  answer: "I perform a granular audit and optimization of your profile, including primary and secondary category precision, geo-tagged image publishing, strategic service menu structuring, local Q&A seeding, review acquisition frameworks, and spam competitor elimination to keep your listing dominant."
}, {
  id: "item-7",
  question: "How do you earn high-quality local backlinks in Gloucestershire?",
  answer: "I leverage genuine local outreach, partnering with respected Cotswold and Gloucestershire publications, regional business chambers, trusted local suppliers, and community sponsorships. I strictly avoid automated PBNs or link schemes, building clean digital PR equity that Google trusts permanently."
}, {
  id: "item-8",
  question: "How does AI Search (Google AI Overviews & ChatGPT) affect local search in Cheltenham?",
  answer: "AI search engines rely heavily on sentiment signals, local directory consensus, real customer reviews, and structured data. My local strategies incorporate Generative Engine Optimization (GEO), ensuring your business is recommended when users ask conversational AI tools for the best service providers in Cheltenham."
}, {
  id: "item-9",
  question: "Will local SEO generate real calls and visits, or just website traffic?",
  answer: "My primary focus is revenue-generating actions—phone calls, direct map directions, contact form conversions, and qualified appointment bookings. High ranking positions mean nothing if they do not convert into actual paying clients for your business."
}, {
  id: "item-10",
  question: "What deliverables and reporting do I receive each month?",
  answer: "You receive concise, plain-English performance reports highlighting rank positions in Cheltenham, Google Business Profile insights, organic conversions, completed optimizations, and actionable next steps. No confusing jargon or vanity metric filler."
}];
export default function Localseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const [searchTerm, setSearchTerm] = useState("");
  const filteredFaqs = faqData.filter(item => item.question.toLowerCase().includes(searchTerm.toLowerCase()) || item.answer.toLowerCase().includes(searchTerm.toLowerCase()));
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4
      }
    }
  };
  return <section id="localseofaq" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-t border-border">
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 size-80 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        <motion.div initial={{
        opacity: 0,
        y: -12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-md border border-secondary/60">
              <MapPin className="size-3.5 text-primary" />
              Cheltenham Market Insights
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Frequently Asked Local SEO Questions
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            Straightforward, expert answers on growing your local search visibility, driving qualified leads, and dominating Google maps in Cheltenham and Gloucestershire.
          </p>

          <div className="mt-8 max-w-md mx-auto relative">
            <div className="relative flex items-center">
              <HelpCircle className="absolute left-3.5 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search local SEO questions..." className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-[box-shadow,border-color]" />
              {searchTerm && <Button variant="ghost" size="sm" onClick={() => setSearchTerm("")} className="absolute right-2 text-xs h-7 px-2">
                  Clear
                </Button>}
            </div>
          </div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="max-w-4xl mx-auto bg-card border border-border rounded-xl p-4 sm:p-8 shadow-sm">
          {filteredFaqs.length === 0 ? <div className="text-center py-12">
              <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" />
              <p className="text-foreground font-medium text-lg mb-1">No matching questions found</p>
              <p className="text-muted-foreground text-sm mb-6">
                Try searching with different terms or ask Yassine directly.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm("")}>
                Reset Search
              </Button>
            </div> : <Accordion type="single" collapsible className="w-full space-y-3">
              {filteredFaqs.map((faq, index) => <motion.div key={faq.id} variants={itemVariants} data-index={index}>
                  <AccordionItem value={faq.id} className="border border-border/80 rounded-lg px-4 sm:px-6 bg-background transition-colors hover:border-primary/40">
                    <AccordionTrigger className="py-4 text-left font-serif text-base sm:text-lg font-semibold text-primary hover:no-underline hover:text-primary/90 flex justify-between items-center gap-4">
                      <span>{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-foreground/90 font-default text-sm sm:text-base leading-relaxed pb-5 pt-1 border-t border-border/40 mt-1">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 max-w-4xl mx-auto rounded-xl bg-gradient-to-br from-primary/5 via-secondary/30 to-muted p-6 sm:p-8 border border-primary/20 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary text-primary-foreground rounded-lg shrink-0 hidden sm:block">
              <Sparkles className="size-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-xs bg-background border-primary/30 text-primary font-medium">
                  Direct Expert Support
                </Badge>
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-serif text-foreground">
                Have a unique local SEO question about your business?
              </h3>
              <p className="text-muted-foreground text-sm font-default mt-1">
                You work directly with me, Yassine Essaadi. Book a free strategy call and I will audit your Cheltenham search presence personally.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-background">
              <a href="tel:+447490984430">
                <PhoneCall className="size-4 mr-1.5 text-primary" />
                <span>+44 7490984430</span>
              </a>
            </Button>
          </div>
        </motion.div>

        <div className="mt-8 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
          <MessageSquare className="size-3.5 text-primary" />
          <span>Tailored Local SEO Strategy • Transparent Monthly Deliverables • No Locked-In Agency Contracts</span>
        </div>
      </div>
    </section>;
}